(() => {
  window.LSFitFinderCategories = window.LSFitFinderCategories || {};

  const states = [['Alabama','AL'],['Alaska','AK'],['Arizona','AZ'],['Arkansas','AR'],['California','CA'],['Colorado','CO'],['Connecticut','CT'],['Delaware','DE'],['District of Columbia','DC'],['Florida','FL'],['Georgia','GA'],['Hawaii','HI'],['Idaho','ID'],['Illinois','IL'],['Indiana','IN'],['Iowa','IA'],['Kansas','KS'],['Kentucky','KY'],['Louisiana','LA'],['Maine','ME'],['Maryland','MD'],['Massachusetts','MA'],['Michigan','MI'],['Minnesota','MN'],['Mississippi','MS'],['Missouri','MO'],['Montana','MT'],['Nebraska','NE'],['Nevada','NV'],['New Hampshire','NH'],['New Jersey','NJ'],['New Mexico','NM'],['New York','NY'],['North Carolina','NC'],['North Dakota','ND'],['Ohio','OH'],['Oklahoma','OK'],['Oregon','OR'],['Pennsylvania','PA'],['Rhode Island','RI'],['South Carolina','SC'],['South Dakota','SD'],['Tennessee','TN'],['Texas','TX'],['Utah','UT'],['Vermont','VT'],['Virginia','VA'],['Washington','WA'],['West Virginia','WV'],['Wisconsin','WI'],['Wyoming','WY']];
  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();
  const escapeHtml = (value) => String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  const normalizeZip = (value) => String(value || '').replace(/\D/g, '').slice(0, 5);
  const sessionStorageKey = 'ls-fit-finder-session-v1';
  const sharedStorageKey = 'ls-fit-finder-session-shared-v1';
  const locationStorageKey = 'ls-soil-location-v1';
  const sharedSessionLifetime = 365 * 24 * 60 * 60 * 1000;

  const parseSession = (value) => {
    try {
      return JSON.parse(value || '{}');
    } catch (error) {
      return {};
    }
  };

  const readCookieSession = () => {
    const item = document.cookie.split('; ').find((value) => value.startsWith(sessionStorageKey + '='));
    return item ? parseSession(decodeURIComponent(item.slice(sessionStorageKey.length + 1))) : {};
  };

  const readSharedSession = () => {
    try {
      return parseSession(window.localStorage.getItem(sharedStorageKey));
    } catch (error) {
      return {};
    }
  };

  const readSession = () => {
    const shared = readSharedSession();
    if (Object.keys(shared).length) return shared;

    const cookie = readCookieSession();
    if (Object.keys(cookie).length) return cookie;

    try {
      return parseSession(window.sessionStorage.getItem(sessionStorageKey));
    } catch (error) {
      return {};
    }
  };

  const writeSession = (value) => {
    const session = { ...value, updatedAt: Date.now() };
    const serialized = JSON.stringify(session);

    try { window.localStorage.setItem(sharedStorageKey, serialized); } catch (error) {}
    try { window.sessionStorage.setItem(sessionStorageKey, serialized); } catch (error) {}
    try { document.cookie = sessionStorageKey + '=' + encodeURIComponent(serialized) + '; path=/; max-age=' + sharedSessionLifetime / 1000 + '; SameSite=Lax'; } catch (error) {}

    return session;
  };

  const readLocation = () => {
    try {
      const location = parseSession(window.localStorage.getItem(locationStorageKey));
      return normalizeZip(location.zip).length === 5 ? location : null;
    } catch (error) {
      return null;
    }
  };

  const findState = (value) => {
    const raw = String(value || '').trim();
    const code = raw.split('-').pop().toUpperCase();
    return states.find(([name, stateCode]) => stateCode === code || name.toLowerCase() === raw.toLowerCase()) || null;
  };

  const formatLocation = (location) => {
    const place = [location.city, location.stateCode || location.state].filter(Boolean).join(', ');
    return [place, location.zip].filter(Boolean).join(' · ');
  };

  const geocodeZip = async (zip) => {
    const postalCode = normalizeZip(zip);
    if (postalCode.length !== 5) throw new Error('Enter a valid 5-digit ZIP code.');

    const url = new URL('https://geocoding-api.open-meteo.com/v1/search');
    Object.entries({ name: postalCode, count: '1', language: 'en', format: 'json', countryCode: 'US' }).forEach(([key, value]) => url.searchParams.set(key, value));

    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error('We could not look up that ZIP code.');

    const data = await response.json();
    const place = data.results?.[0];
    const stateMatch = findState(place?.admin1);
    if (!place || !stateMatch) throw new Error('Enter a valid U.S. ZIP code.');

    return {
      zip: postalCode,
      latitude: place.latitude,
      longitude: place.longitude,
      city: place.name || '',
      state: stateMatch[0],
      stateCode: stateMatch[1]
    };
  };

  const detectZip = async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4000);

    try {
      const response = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=en', {
        credentials: 'omit',
        signal: controller.signal
      });
      if (!response.ok) return null;

      const data = await response.json();
      if (String(data?.countryCode || '').toUpperCase() !== 'US') return null;

      const zip = normalizeZip(data?.postcode);
      if (zip.length !== 5) return null;

      return geocodeZip(zip);
    } catch (error) {
      return null;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const initialize = (root) => {
    if (!root || root.dataset.fitFinderInitialized === 'true') return;

    const category = window.LSFitFinderCategories[root.dataset.fitCategory];
    if (!category) return;

    root.dataset.fitFinderInitialized = 'true';

    const regionsNode = root.querySelector('[data-fit-regions-data]');
    const regions = regionsNode ? JSON.parse(regionsNode.textContent).regions || [] : [];
    const locationStep = root.querySelector('[data-fit-step="state"]');
    const locationLegend = locationStep?.querySelector('.ls-v2-fit-finder__question-title');

    const el = {
      header: root.querySelector('.ls-v2-fit-finder__header'),
      stepLabel: root.querySelector('.ls-v2-fit-finder__step-label'),
      steps: Array.from(root.querySelectorAll('[data-fit-step]')),
      options: Array.from(root.querySelectorAll('[data-fit-option]')),
      sliders: Array.from(root.querySelectorAll('[data-fit-slider]')),
      backButtons: Array.from(root.querySelectorAll('[data-fit-back]')),
      result: root.querySelector('[data-fit-result]'),
      resultIcon: root.querySelector('[data-fit-result-icon]'),
      resultTitle: root.querySelector('[data-fit-result-title]'),
      resultCopy: root.querySelector('[data-fit-result-copy]'),
      lawnContext: root.querySelector('[data-fit-lawn-context]'),
      lawnContextName: root.querySelector('[data-fit-lawn-context-name]'),
      stateContext: root.querySelector('[data-fit-state-context]'),
      stateContextName: root.querySelector('[data-fit-state-context-name]'),
      fallback: root.querySelector('[data-fit-fallback]'),
      restart: root.querySelector('[data-fit-restart]'),
      stateSelect: root.querySelector('[data-fit-state]'),
      stateContinue: root.querySelector('[data-fit-state-continue]'),
      statePrompt: root.querySelector('[data-fit-state-prompt]'),
      stateChange: root.querySelector('[data-fit-state-change]'),
      zipInput: null
    };

    if (locationLegend) locationLegend.textContent = 'What is your ZIP code?';

    if (el.stateSelect) {
      states.forEach(([name, code]) => el.stateSelect.add(new Option(name, code)));
      el.stateSelect.hidden = true;

      const zipInput = document.createElement('input');
      zipInput.type = 'text';
      zipInput.inputMode = 'numeric';
      zipInput.autocomplete = 'postal-code';
      zipInput.maxLength = 5;
      zipInput.placeholder = 'ZIP code';
      zipInput.className = el.stateSelect.className;
      zipInput.setAttribute('data-fit-zip', '');
      zipInput.hidden = true;
      el.stateSelect.insertAdjacentElement('beforebegin', zipInput);
      el.zipInput = zipInput;
    }

    const answers = { grass: '', sun: '', sunValue: '', state: '', region: '', zip: '' };
    const state = { detectedLocation: null, lookupFinished: false, lookupStarted: false };
    const savedSession = readSession();
    let activeLocation = readLocation();

    const clearResult = () => {
      if (el.result) {
        el.result.hidden = true;
        el.result.classList.remove('is-match', 'is-mismatch');
      }
      if (el.fallback) el.fallback.hidden = true;
      if (el.lawnContext) el.lawnContext.hidden = true;
      if (el.stateContext) el.stateContext.hidden = true;
      category.clear?.(ctx);
    };

    const hideSteps = () => el.steps.forEach((step) => { step.hidden = true; });

    const revealLawnContext = () => {
      if (!el.lawnContext || !answers.grass) return;
      el.lawnContext.hidden = false;
      if (el.lawnContextName) el.lawnContextName.textContent = answers.grass;
    };

    const revealStateContext = () => {
      if (!el.stateContext || !activeLocation || !answers.state) return;
      el.stateContext.hidden = false;
      if (el.stateContextName) el.stateContextName.textContent = formatLocation(activeLocation);
    };

    const showResult = ({ match, icon, title, copy, fallback = false }) => {
      clearResult();
      hideSteps();
      if (el.header) el.header.hidden = true;
      el.result.hidden = false;
      el.result.classList.add(match ? 'is-match' : 'is-mismatch');
      el.resultIcon.textContent = icon;
      el.resultTitle.textContent = title;
      el.resultCopy.textContent = copy;
      if (fallback && el.fallback) el.fallback.hidden = false;
      revealLawnContext();
      revealStateContext();
    };

    const showMismatch = (type, label) => showResult({
      match: false,
      icon: '!',
      fallback: true,
      title: type === 'grass' ? 'Not recommended for ' + label : 'Not the best fit for this area',
      copy: type === 'grass' ? (root.dataset.productTitle || 'This product') + ' is not a recommended match for ' + label + '.' : (root.dataset.productTitle || 'This product') + ' is not the best fit for ' + label + ' conditions.'
    });

    const showStep = (name) => {
      clearResult();
      if (el.stepLabel) el.stepLabel.textContent = 'STEP 1 OF 2';
      if (el.header) el.header.hidden = name !== 'grass' && name !== 'sun';
      el.steps.forEach((step) => {
        const active = step.dataset.fitStep === name;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
    };

    const showZipEntry = () => {
      if (!el.zipInput || !el.stateContinue) return;
      if (el.statePrompt) el.statePrompt.textContent = 'Enter your ZIP code';
      el.zipInput.hidden = false;
      el.zipInput.value = activeLocation?.zip || savedSession.zip || '';
      if (el.stateChange) el.stateChange.hidden = true;
      el.stateContinue.hidden = false;
      el.stateContinue.disabled = normalizeZip(el.zipInput.value).length !== 5;
      el.stateContinue.textContent = 'Continue';
    };

    const requestState = () => {
      showStep('state');
      showZipEntry();
    };

    const ctx = { root, el, answers, state, normalize, escapeHtml, clearResult, hideSteps, showStep, showResult, showMismatch, requestState, revealLawnContext, revealStateContext };
    category.initialize?.(ctx);

    const selectOption = (option) => {
      const type = option.dataset.fitType;
      root.querySelectorAll('[data-fit-option][data-fit-type="' + type + '"]').forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });
      option.classList.add('is-selected');
      option.setAttribute('aria-pressed', 'true');

      if (type === 'grass') {
        answers.grass = option.dataset.fitLabel || '';
        writeSession({ ...readSession(), grass: answers.grass });
        answers.sun = '';
        answers.sunValue = '';
        root.querySelectorAll('[data-fit-option][data-fit-type="sun"]').forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-pressed', 'false');
        });
      }

      if (type === 'sun') {
        answers.sun = option.dataset.fitLabel || '';
        answers.sunValue = option.dataset.fitValue || '';
      }
    };

    const applyState = (code, detected) => {
      const selected = states.find((item) => item[1] === String(code || '').toUpperCase());
      if (!selected) return;

      if (el.stateSelect) el.stateSelect.value = selected[1];
      answers.state = selected[1];
      const match = regions.find((region) => (region.stateCodes || []).includes(selected[1]));
      answers.region = selected[1] === 'FL' ? 'Florida' : match?.name || '';
      writeSession({ ...readSession(), state: selected[1], zip: answers.zip || activeLocation?.zip || '' });
      category.onState?.(ctx, detected);
    };

    const applyLocation = (location, detected) => {
      const stateMatch = findState(location?.stateCode || location?.state);
      const zip = normalizeZip(location?.zip);
      if (!stateMatch || zip.length !== 5) return;

      activeLocation = {
        zip,
        latitude: location.latitude,
        longitude: location.longitude,
        city: location.city || '',
        state: stateMatch[0],
        stateCode: stateMatch[1]
      };

      try { window.localStorage.setItem(locationStorageKey, JSON.stringify(activeLocation)); } catch (error) {}

      answers.zip = zip;
      answers.state = stateMatch[1];
      const match = regions.find((region) => (region.stateCodes || []).includes(stateMatch[1]));
      answers.region = stateMatch[1] === 'FL' ? 'Florida' : match?.name || '';

      if (el.stateSelect) el.stateSelect.value = stateMatch[1];
      if (el.zipInput) {
        el.zipInput.value = zip;
        el.zipInput.hidden = true;
      }
      if (el.statePrompt) el.statePrompt.textContent = formatLocation(activeLocation);
      if (el.stateChange) el.stateChange.hidden = false;
      if (el.stateContinue) el.stateContinue.hidden = true;

      writeSession({
        ...readSession(),
        zip,
        city: activeLocation.city,
        state: stateMatch[1],
        latitude: activeLocation.latitude,
        longitude: activeLocation.longitude
      });

      category.onState?.(ctx, detected);
    };

    ctx.applyState = applyState;
    ctx.applyLocation = applyLocation;

    if (el.lawnContext) {
      el.lawnContext.addEventListener('click', () => {
        showStep('grass');
      });
    }

    if (el.stateContext) {
      el.stateContext.addEventListener('click', () => {
        showStep('state');
        showZipEntry();
      });
    }

    if (el.zipInput && el.stateContinue) {
      el.zipInput.addEventListener('input', () => {
        el.zipInput.value = normalizeZip(el.zipInput.value);
        el.stateContinue.disabled = el.zipInput.value.length !== 5;
      });

      el.stateContinue.addEventListener('click', async () => {
        const zip = normalizeZip(el.zipInput.value);
        if (zip.length !== 5) return;

        el.stateContinue.disabled = true;
        el.stateContinue.textContent = 'Checking…';

        try {
          const location = await geocodeZip(zip);
          applyLocation(location, false);
        } catch (error) {
          if (el.statePrompt) el.statePrompt.textContent = error.message;
          el.stateContinue.disabled = false;
          el.stateContinue.textContent = 'Continue';
        }
      });
    }

    if (el.stateChange) {
      el.stateChange.addEventListener('click', () => {
        showZipEntry();
        el.zipInput?.focus();
      });
    }

    if (activeLocation) applyLocation(activeLocation, true);

    let startLocationLookup = () => {};
    startLocationLookup = () => {
      if (state.lookupStarted || answers.state) return;

      state.lookupStarted = true;
      detectZip().then((location) => {
        state.detectedLocation = location;
        if (location && !answers.state) applyLocation(location, true);
      }).finally(() => {
        state.lookupFinished = true;

        if (answers.grass && !answers.state) category.onLookupMiss?.(ctx);
        category.onLookupFinished?.(ctx);
      });
    };

    el.options.forEach((option) => option.addEventListener('click', () => {
      selectOption(option);
      const shouldFindLocation = option.dataset.fitType === 'grass' || (root.dataset.fitCategory === 'seed' && option.dataset.fitType === 'sun');
      if (shouldFindLocation) startLocationLookup();
      category.onOption?.(ctx, option);
    }));

    const savedGrass = normalize(savedSession.grass || '');
    const savedGrassOption = el.options.find((option) => option.dataset.fitType === 'grass' && normalize(option.dataset.fitLabel) === savedGrass);
    if (savedGrassOption) {
      selectOption(savedGrassOption);
      if (answers.state) category.onOption?.(ctx, savedGrassOption);
    }

    el.backButtons.forEach((button) => button.addEventListener('click', () => showStep(button.dataset.fitBack || 'grass')));

    if (el.restart) {
      el.restart.addEventListener('click', () => {
        answers.grass = '';
        answers.sun = '';
        answers.sunValue = '';
        writeSession({ ...readSession(), grass: '' });
        el.options.forEach((option) => {
          option.classList.remove('is-selected');
          option.setAttribute('aria-pressed', 'false');
        });
        el.sliders.forEach((slider) => { slider.scrollLeft = 0; });
        showStep(category.restartStep || 'grass');
      });
    }

    const amount = (slider) => {
      const card = slider.querySelector('[data-fit-option]');
      if (!card) return slider.clientWidth * .8;
      const styles = window.getComputedStyle(slider);
      return card.getBoundingClientRect().width + parseFloat(styles.columnGap || styles.gap || 0);
    };

    const arrows = (slider) => {
      const step = slider.closest('[data-fit-step]');
      const previous = step?.querySelector('[data-fit-prev]');
      const next = step?.querySelector('[data-fit-next]');
      if (!previous || !next) return;
      const max = slider.scrollWidth - slider.clientWidth;
      previous.disabled = slider.scrollLeft <= 4;
      next.disabled = slider.scrollLeft >= max - 4;
    };

    el.sliders.forEach((slider) => {
      const step = slider.closest('[data-fit-step]');
      const previous = step?.querySelector('[data-fit-prev]');
      const next = step?.querySelector('[data-fit-next]');
      if (previous) previous.addEventListener('click', () => slider.scrollBy({ left: -amount(slider), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => slider.scrollBy({ left: amount(slider), behavior: 'smooth' }));
      slider.addEventListener('scroll', () => arrows(slider), { passive: true });
      arrows(slider);
    });

    window.addEventListener('resize', () => el.sliders.forEach(arrows));
  };

  document.querySelectorAll('[data-ls-v2-fit-finder]').forEach(initialize);
})();
